import { useMutation } from '@tanstack/react-query';

import type { TypeCodeState, TypeContract, TypeSpecialMedicalExamination } from '../../types';
import { API_VERSION_V2 } from '~/constants';
import { axiosInstance } from '~/services/fetchServiceV2';
import { type TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

export type TypePrivacyInfo = {
  /** 주민등록번호 */
  juminNo: string;
  /** 주민등록번호 (마스킹) */
  maskedJuminNo?: string;
  /** 주소 */
  address: string;
  /** 상세주소 */
  detailAddress: string;
  /** 은행 */
  financialInstitution: TypeCodeState;
  /** 계좌번호 */
  accountNo: string;
  /** 계좌번호 (마스킹) */
  maskedAccountNo?: string;
  /** 우편번호 */
  postCode: string;
  /** 계좌주명 */
  accountName?: string;
};

export type TypeAgreeContractTerms = {
  code: string;
  message: string;
  data: {
    laborAccountId: number;
    loginId: string;
    name: string;
    status: TypeCodeState;
    contractType: TypeContract;
    phoneNumber: string;
    kurlyWorksId: string | null;
    kurlyContract: { cluster: TypeCodeState; center: TypeCodeState; workPart: TypeCodeState } | null;
    latestCommute: {
      cluster: TypeCodeState;
      center: TypeCodeState;
    } | null;
    specialMedicalExamination: TypeSpecialMedicalExamination | null;
    termsAgreed: boolean;
    privacyInfo: TypePrivacyInfo | null;
  } | null;
};

/**
 * @description 계약 전 유의사항 확인 및 동의
 */
export const useAgreeContractTerms = () => {
  const { mutateAsync } = useMutation({
    mutationFn: () => {
      const endpoint = '/labor/agree-terms-and-conditions';
      return axiosInstance.put<TypeAgreeContractTerms>(endpoint, undefined, {
        headers: {
          version: API_VERSION_V2,
        },
      });
    },
  });

  const requestAgreeContractTerms = async ({
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TypeAgreeContractTerms) => void;
    onError?: (err: TypeErrorFormat) => void;
  }) => {
    requestHelper(() => mutateAsync(), onSuccess, onError);
  };

  return requestAgreeContractTerms;
};
